import { Box, Divider, Flex, Text } from '@chakra-ui/react'
import { FHRAction, FHRCategory, FHRDeceleration, FHRMethod, FHRVariability } from '@hb/shared'
import { format } from 'date-fns'
import type { FC, MouseEvent } from 'react'
import {
  CartesianGrid,
  ComposedChart,
  Line,
  Tooltip as RechartsTooltip,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts'

import { ChartLegend } from './ChartLegend'
import type { LaborChartXAxisProps } from './DilationStationGraph'

// ── Helpers ────────────────────────────────────────────────────────────────

const bpmColor = (bpm: number) =>
  bpm < 100 || bpm > 180 ? '#E53E3E' : bpm < 110 || bpm > 160 ? '#ED8936' : '#48BB78'

const categoryColor = (c: FHRCategory) =>
  c === FHRCategory.I ? '#48BB78' : c === FHRCategory.II ? '#F6AD55' : '#FC8181'

const categoryLabel = (c: FHRCategory) =>
  c === FHRCategory.I ? 'Normal' : c === FHRCategory.II ? 'Indeterminate' : 'Abnormal'

const variabilityColor = (v: FHRVariability) => {
  switch (v) {
    case FHRVariability.ABSENT:
      return '#FC8181'
    case FHRVariability.MINIMAL:
      return '#F6AD55'
    case FHRVariability.MARKED:
      return '#ECC94B'
    default:
      return '#48BB78'
  }
}

const fmtVariability = (v: FHRVariability) => v.charAt(0).toUpperCase() + v.slice(1)

const decelerationColor = (d: FHRDeceleration) => {
  switch (d) {
    case FHRDeceleration.NONE:
      return '#48BB78'
    case FHRDeceleration.EARLY:
      return '#F6AD55'
    case FHRDeceleration.VARIABLE:
      return '#ED8936'
    default:
      return '#FC8181'
  }
}

const fmtDeceleration = (d: FHRDeceleration) => d.charAt(0).toUpperCase() + d.slice(1)

const fmtMethod = (m: FHRMethod) => {
  switch (m) {
    case FHRMethod.DOPPLER:
      return 'Doppler'
    case FHRMethod.FETOSCOPE:
      return 'Fetoscope'
    case FHRMethod.INTERNAL_SCALP_ELECTRODE:
      return 'Scalp Electrode'
  }
}

const fmtAction = (a: FHRAction) => {
  switch (a) {
    case FHRAction.POSITION_CHANGE:
      return 'Position change'
    case FHRAction.OXYGEN:
      return 'Oxygen'
    case FHRAction.FLUID_BOLUS:
      return 'Fluid bolus'
    case FHRAction.IV_ACCESS:
      return 'IV access'
    case FHRAction.NOTIFY_PROVIDER:
      return 'Notify provider'
    case FHRAction.AMNIOINFUSION:
      return 'Amnioinfusion'
  }
}

// ── Shared FHR Exam Display ────────────────────────────────────────────────

export interface FHRExamDisplayProps {
  baselineBpm: number
  variability: FHRVariability
  accelerations: boolean
  decelerations: FHRDeceleration
  actions: FHRAction[]
  category: FHRCategory
  method: FHRMethod
}

/** Full FHR exam summary — used in both the FHR tooltip and the entry click popover. */
export const FHRExamDisplay = ({
  baselineBpm,
  variability,
  accelerations,
  decelerations,
  actions,
  category,
  method,
}: FHRExamDisplayProps) => {
  const bpmCol = bpmColor(baselineBpm)
  const catCol = categoryColor(category)
  const varCol = variabilityColor(variability)
  const decelCol = decelerationColor(decelerations)

  return (
    <Flex direction="column" gap={0.5}>
      {/* Primary: BPM + category */}
      <Flex justify="space-between" align="center">
        <Text fontSize="xs" color="gray.400">
          FHR Baseline
        </Text>
        <Flex align="baseline" gap={1}>
          <Text fontSize="sm" fontWeight={700} color={bpmCol}>
            {baselineBpm}
          </Text>
          <Text fontSize="xs" color={bpmCol}>
            bpm
          </Text>
        </Flex>
      </Flex>
      <Flex justify="space-between" align="center">
        <Text fontSize="xs" color="gray.400">
          Category
        </Text>
        <Flex align="baseline" gap={1}>
          <Text fontSize="xs" fontWeight={700} color={catCol}>
            Cat.&nbsp;{category}
          </Text>
          <Text fontSize="xs" color={catCol} opacity={0.8}>
            {categoryLabel(category)}
          </Text>
        </Flex>
      </Flex>

      {/* Variability / accelerations / decelerations */}
      <Divider my={1} />
      <Flex justify="space-between" align="center">
        <Text fontSize="xs" color="gray.400">
          Variability
        </Text>
        <Text fontSize="xs" fontWeight={600} color={varCol}>
          {fmtVariability(variability)}
        </Text>
      </Flex>
      <Flex justify="space-between" align="center">
        <Text fontSize="xs" color="gray.400">
          Accelerations
        </Text>
        <Text fontSize="xs" fontWeight={600} color={accelerations ? '#48BB78' : 'gray.400'}>
          {accelerations ? 'Present' : 'Absent'}
        </Text>
      </Flex>
      <Flex justify="space-between" align="center">
        <Text fontSize="xs" color="gray.400">
          Decelerations
        </Text>
        <Text fontSize="xs" fontWeight={600} color={decelCol}>
          {fmtDeceleration(decelerations)}
        </Text>
      </Flex>

      {/* Method + actions */}
      <Divider my={1} />
      <Flex justify="space-between" align="center">
        <Text fontSize="xs" color="gray.400">
          Method
        </Text>
        <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.300' }}>
          {fmtMethod(method)}
        </Text>
      </Flex>
      {actions.length > 0 ? (
        <Flex justify="space-between" align="flex-start">
          <Text fontSize="xs" color="gray.400" mt={0.5}>
            Actions
          </Text>
          <Flex direction="column" align="flex-end" gap={0}>
            {actions.map(a => (
              <Text key={a} fontSize="xs" color="gray.600" _dark={{ color: 'gray.300' }}>
                {fmtAction(a)}
              </Text>
            ))}
          </Flex>
        </Flex>
      ) : null}
    </Flex>
  )
}

// ── Legend items ───────────────────────────────────────────────────────────

const FHR_LEGEND_ITEMS = [
  { kind: 'line' as const, label: 'FHR baseline', color: '#E53E3E' },
  {
    kind: 'band' as const,
    label: 'Normal range (110–160 bpm)',
    fill: 'rgba(154,230,180,0.12)',
  },
  {
    kind: 'line' as const,
    label: '110 / 160 bpm thresholds',
    color: '#68D391',
    dashed: true,
  },
]

// ── Data + Tooltip ─────────────────────────────────────────────────────────

export interface FHRPoint {
  time: number
  bpm: number
  variability?: FHRVariability
  accelerations?: boolean
  decelerations?: FHRDeceleration
  actions?: FHRAction[]
  category?: FHRCategory
  method?: FHRMethod
  entryId?: string
}

interface FHRTooltipEntry {
  dataKey: string
  value: number | null | undefined
  payload: FHRPoint
}

const safeFormatTime = (ms: number) => {
  try {
    return format(new Date(ms), 'HH:mm')
  } catch {
    return ''
  }
}

const FHRTooltip = ({ active, payload }: { active?: boolean; payload?: FHRTooltipEntry[] }) => {
  if (!active || !payload?.length) return null
  const pt = payload[0]?.payload
  if (!pt) return null

  const { time, bpm, variability, accelerations, decelerations, actions, category, method } = pt

  const hasFullData =
    variability != null &&
    accelerations != null &&
    decelerations != null &&
    actions != null &&
    category != null &&
    method != null

  return (
    <Box
      bg="white"
      _dark={{ bg: 'gray.800', borderColor: 'whiteAlpha.300' }}
      border="1px solid"
      borderColor="gray.200"
      borderRadius={8}
      px={3}
      py={2.5}
      shadow="md"
      fontFamily="hero-new"
      fontSize="sm"
      minW="220px"
      maxW="300px">
      {time != null ? (
        <Text fontWeight={600} color="gray.500" _dark={{ color: 'gray.400' }} mb={1.5}>
          {safeFormatTime(time)}
        </Text>
      ) : null}
      {hasFullData ? (
        <>
          <Divider mb={1.5} />
          <FHRExamDisplay
            baselineBpm={bpm}
            variability={variability}
            accelerations={accelerations}
            decelerations={decelerations}
            actions={actions}
            category={category}
            method={method}
          />
        </>
      ) : (
        <>
          <Divider mb={1.5} />
          <Text color="#E53E3E">FHR: {bpm} bpm</Text>
        </>
      )}
    </Box>
  )
}

// ── Graph ──────────────────────────────────────────────────────────────────

export const FetalHeartRateGraph: FC<{
  fhrPoints: FHRPoint[]
  xAxisProps: LaborChartXAxisProps
  gridColor: string
  tickStyle: { fontFamily: string; fontSize: number; fill: string }
  onChartClick: (data: any, event: MouseEvent) => void
}> = ({ fhrPoints, xAxisProps, gridColor, tickStyle, onChartClick }) => (
  <>
    <Box w="100%" h="200px" pl={1} pr={4} cursor="pointer">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={fhrPoints}
          margin={{ top: 8, right: 52, left: 8, bottom: 8 }}
          syncId="labor"
          onClick={onChartClick}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis {...xAxisProps} />
          <YAxis
            domain={[80, 200]}
            ticks={[80, 100, 110, 120, 140, 160, 180, 200]}
            tick={tickStyle}
            label={{
              value: 'bpm',
              angle: -90,
              dx: -10,
              position: 'middle',
              style: { fontFamily: 'hero-new', fontSize: 16, fill: '#E53E3E' },
            }}
          />
          <ReferenceArea y1={110} y2={160} fill="rgba(154,230,180,0.12)" stroke="none" />
          <ReferenceLine
            y={110}
            stroke="#68D391"
            strokeDasharray="4 4"
            label={{
              value: '110',
              position: 'right',
              style: { fontSize: 9, fontFamily: 'hero-new', fill: '#2F855A' },
            }}
          />
          <ReferenceLine
            y={160}
            stroke="#68D391"
            strokeDasharray="4 4"
            label={{
              value: '160',
              position: 'right',
              style: { fontSize: 9, fontFamily: 'hero-new', fill: '#2F855A' },
            }}
          />
          <RechartsTooltip content={<FHRTooltip />} />
          <Line
            dataKey="bpm"
            stroke="#E53E3E"
            strokeWidth={2}
            dot={{ r: 4, fill: '#E53E3E', stroke: '#fff', strokeWidth: 1.5 }}
            activeDot={{ r: 7 }}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
    <ChartLegend items={FHR_LEGEND_ITEMS} />
  </>
)
